import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from './MyPrivateTutor.module.scss';
import classes from 'classnames';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import GoToTutor from '@/components/GoToTutor/GoToTutor';
import { EllReport } from '@mavatar/melingo-voice-and-avatar-sdk';
import {
  GetChatsPageResponse,
  GetMistakesResponse,
} from '@mavatar/melingo-voice-and-avatar-sdk/dist/Tutor/EllReport';
import DropDown, { Option } from '@/components/GenericTable/DropDown/DropDown';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import SVG from 'react-inlinesvg';
import starIcon from '@/assets/svg/star.svg';
import starEmptyIcon from '@/assets/svg/star-empty.svg';
import { GroupStudentProgress } from '@/types/progress';
import UseScreenSize from '@/hooks/UseScreenSize';
import { PrivateTutorTypes } from '@/pages/dashboard/tutor-chat/TutorChat';
import TutorTopicsUtils, { QuizStatus } from '@/utils/tutorTopics';
import CommonMistakes from '@/components/TutorDashboard/CommonMistakes/CommonMistakes';
import {
  type FilterField,
  type FilterValue,
} from '@/components/school-management/BimloaFilter/BimloaFilter';
import { NumberRangeFilterValue } from '@/components/school-management/BimloaFilter/NumberRangeFilter';

const CHATS_PAGE_SIZE = 12;

export interface SortData {
  field:
    | 'conversations'
    | 'created_date'
    | 'number_of_mistakes'
    | 'topic'
    | 'max_rank'
    | 'status'
    | 'score';
  isDescending: boolean;
  labelKey: string;
}

export const sortOptions: SortData[] = [
  {
    field: 'created_date',
    isDescending: false,
    labelKey: 'my_tutor_page.sortOptions.createdDateAscending',
  },
  {
    field: 'created_date',
    isDescending: true,
    labelKey: 'my_tutor_page.sortOptions.createdDateDescending',
  },
  {
    field: 'topic',
    isDescending: false,
    labelKey: 'my_tutor_page.sortOptions.topicAscending',
  },
  {
    field: 'topic',
    isDescending: true,
    labelKey: 'my_tutor_page.sortOptions.topicDescending',
  },
  {
    field: 'max_rank',
    isDescending: false,
    labelKey: 'my_tutor_page.sortOptions.rankAscending',
  },
  {
    field: 'max_rank',
    isDescending: true,
    labelKey: 'my_tutor_page.sortOptions.rankDescending',
  },
];

export const sortMistakesOptions: SortData[] = [
  {
    field: 'created_date',
    isDescending: false,
    labelKey: 'my_tutor_page.sortOptions.createdDateAscending',
  },
  {
    field: 'created_date',
    isDescending: true,
    labelKey: 'my_tutor_page.sortOptions.createdDateDescending',
  },
  {
    field: 'score',
    isDescending: false,
    labelKey: 'my_tutor_page.sortOptions.scoreAscending',
  },
  {
    field: 'score',
    isDescending: true,
    labelKey: 'my_tutor_page.sortOptions.scoreDescending',
  },
  // {
  //   field: 'status',
  //   isDescending: false,
  //   labelKey: 'my_tutor_page.sortOptions.statusAscending',
  // },
  // {
  //   field: 'status',
  //   isDescending: true,
  //   labelKey: 'my_tutor_page.sortOptions.statusDescending',
  // },
];

interface SelfViewProps {
  teachersClasses: any;
  selectedSortData: SortData;
  setSelectedSortData: React.Dispatch<React.SetStateAction<SortData>>;
  selectedMistakesSortData: SortData;
  setSelectedMistakesSortData: React.Dispatch<React.SetStateAction<SortData>>;
  openReport: (report: GetChatsPageResponse, userId: string) => void;
  startConversation: (chat: GetChatsPageResponse) => void;
  setStudents: React.Dispatch<React.SetStateAction<{ [student_id: string]: GroupStudentProgress }>>;
}

const SelfView = ({
  teachersClasses,
  selectedSortData,
  setSelectedSortData,
  selectedMistakesSortData,
  setSelectedMistakesSortData,
  openReport,
  startConversation,
  setStudents,
}: SelfViewProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [mistakes, setMistakes] = useState<GetMistakesResponse[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [isFirstHistoryLoading, setIsFirstHistoryLoading] = useState(true);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const lastHistoryLoadingTempData = useRef<number>(0);
  const [hasData, setHasData] = useState<boolean>(false);
  const [history, setHistory] = useState<GetChatsPageResponse[]>([]);
  const { user } = useAppSelector(authSelector);
  const { windowWidth, isMobile } = UseScreenSize();

  const [mistakesFilters, setMistakesFilters] = useState<Record<string, FilterValue>>({
    type: [],
    status: [],
    score: 'All',
  });

  const mistakesFilterFields: FilterField[] = useMemo(() => {
    const types = Array.from(
      new Set(
        (mistakes || []).map(m => {
          return m.enhancementAreaSimplified || m.enhancementArea;
        })
      )
    ).sort();

    const statuses = Array.from(
      new Set(
        (mistakes || []).map(m => {
          const status = TutorTopicsUtils.getQuizStatus(m.quizzes[0].Score);
          return t(`my_tutor_page.quizStatus.${status}`);
        })
      )
    ).sort();

    return [
      {
        key: 'status',
        label: t('my_tutor_page.filterOptions.status').toString(),
        options: statuses,
        multi: true,
        searchable: true,
        searchPlaceholder: t('my_tutor_page.filterOptions.statusSearchPlaceholder').toString(),
      },
      {
        key: 'type',
        label: t('my_tutor_page.filterOptions.enhancementArea').toString(),
        options: types,
        multi: true,
        searchable: true,
        searchPlaceholder: t(
          'my_tutor_page.filterOptions.enhancementAreaSearchPlaceholder'
        ).toString(),
      },
      {
        key: 'score',
        label: t('my_tutor_page.filterOptions.score').toString(),
        options: [],
        numberRangeMode: {
          min: 0,
          max: 100,
        },
      },
    ];
  }, [mistakes, t]);

  const userId = useMemo(() => {
    return user?.id;
  }, [user]);

  useEffect(() => {
    let isActive = true;

    setIsLoading(true);

    let data: any[] | null = null;

    const initData = async () => {
      try {
        const userId = user?.id;

        data = await Promise.all([
          EllReport.getMistakes({
            userId: userId,
            orderBy: 'quizzes',
            isOrderDESC: true,
          }),
        ]);
      } catch (ex) {}

      if (!isActive) {
        return;
      }

      if (data) {
        const mistakes: GetMistakesResponse[] = [];

        const createMistakes = (fromMistake: GetMistakesResponse) => {
          const mistake: GetMistakesResponse = {
            ...fromMistake,
            quizzes: [],
            count: 0,
            examples: [],
            score: -1,
            numberOfQuizzes: 0,
            numberOfDoneQuizzes: 0,
          };
          return mistake;
        };

        (data[0] as GetMistakesResponse[]).forEach(mistake => {
          const practiceMistakes: GetMistakesResponse = createMistakes(mistake);
          const improvementMistakes: GetMistakesResponse = createMistakes(mistake);
          const completedMistakes: GetMistakesResponse = createMistakes(mistake);

          let canCalcPracticeData: boolean = true;

          (mistake.quizzes || []).forEach(quiz => {
            const status = TutorTopicsUtils.getQuizStatus(quiz.Score);
            const hasQuiz = !!(quiz.Quiz || '').trim();

            const calcData = (pull: GetMistakesResponse) => {
              pull.count += Number(quiz.Count || 0);

              if (hasQuiz) {
                pull.quizzes.push(quiz);

                if (pull.score === -1 || pull.score > quiz.Score) {
                  pull.score = quiz.Score;
                }

                pull.numberOfQuizzes += 1;

                if (quiz.IsQuizDone) {
                  pull.numberOfDoneQuizzes += 1;
                }
              }

              try {
                if (quiz.Examples && quiz.Examples.trim()) {
                  const examples = JSON.parse(quiz.Examples);
                  pull.examples = [...(pull.examples || []), ...examples];
                }
              } catch (ex) {}
            };

            if (canCalcPracticeData && !hasQuiz) {
              calcData(practiceMistakes);
            }

            if (hasQuiz) {
              canCalcPracticeData = false;

              switch (status) {
                case QuizStatus.PRACTICE:
                  calcData(practiceMistakes);
                  break;
                case QuizStatus.IMPROVEMENT:
                  calcData(improvementMistakes);
                  break;
                case QuizStatus.COMPLETED:
                  calcData(completedMistakes);
                  break;
              }
            }
          });

          if (practiceMistakes.quizzes.length > 0) {
            mistakes.push(practiceMistakes);
          }
          if (improvementMistakes.quizzes.length > 0) {
            mistakes.push(improvementMistakes);
          }
          if (completedMistakes.quizzes.length > 0) {
            mistakes.push(completedMistakes);
          }
        });

        setMistakes(mistakes);
      }

      setIsLoading(false);
    };

    initData();

    return () => {
      isActive = false;
    };
  }, [user]);

  useEffect(() => {
    let isActive = true;

    setIsHistoryLoading(true);
    setHistory([]);
    setStudents({});

    let history: GetChatsPageResponse[] = [];
    lastHistoryLoadingTempData.current = lastHistoryLoadingTempData.current + 1;

    const initData = async () => {
      try {
        const userId = user?.id;

        history = await EllReport.getChatsPage({
          userId: userId,
          orderBy:
            selectedSortData.field === 'status' || selectedSortData.field === 'score'
              ? 'created_date'
              : selectedSortData.field,
          isOrderDESC: selectedSortData.isDescending,
          limit: CHATS_PAGE_SIZE,
          offset: 0,
        });
      } catch (ex) {}

      if (!isActive) {
        return;
      }

      setIsHistoryLoading(false);
      setIsFirstHistoryLoading(false);
      setHistory(history);
      setHasData(history.length > 0);
      setHasMoreHistory(history.length === CHATS_PAGE_SIZE);
    };

    initData();

    return () => {
      isActive = false;
    };
  }, [user, selectedSortData]);

  const getMoreHistory = useCallback(() => {
    setIsHistoryLoading(true);

    let newHistory: GetChatsPageResponse[] = [];
    const loadingTempData = lastHistoryLoadingTempData.current + 1;
    lastHistoryLoadingTempData.current = loadingTempData;

    const initData = async () => {
      try {
        const userId = user?.id;

        newHistory = await EllReport.getChatsPage({
          userId: userId,
          orderBy:
            selectedSortData.field === 'status' || selectedSortData.field === 'score'
              ? 'created_date'
              : selectedSortData.field,
          isOrderDESC: selectedSortData.isDescending,
          limit: CHATS_PAGE_SIZE,
          offset: history.length,
        });
      } catch (ex) {}

      if (loadingTempData !== lastHistoryLoadingTempData.current) {
        return;
      }

      setIsHistoryLoading(false);
      setHistory([...history, ...newHistory]);
      setHasMoreHistory(newHistory.length === CHATS_PAGE_SIZE);
    };

    initData();
  }, [user, selectedSortData, history]);

  const sortDropdownOption: Option[] = useMemo(() => {
    return sortOptions.map(sort => {
      return {
        label: t(sort.labelKey),
        value: sort.labelKey,
        data: sort,
      };
    });
  }, [t]);

  const onSortSelect = useCallback((value: Option) => {
    setSelectedSortData(value.data as SortData);
  }, []);

  const historyBlock = useMemo(() => {
    if (history.length === 0 && !isHistoryLoading) {
      return;
    }

    return (
      <div className={classNames.history}>
        <div className={classNames.historyHader}>
          <div className={classNames.historyHaderTitel}>
            <span>{t('my_tutor_page.yourChats')}</span>
            <div className={classNames.historyHaderSubtitle}>
              {t('my_tutor_page.jumpBackInSubtitle')}
            </div>
          </div>
          <div className={classNames.historyHaderTools}>
            <div className={classNames.historyHaderTool}>
              <DropDown
                options={sortDropdownOption}
                staticTitle={t('sortBy').toString()}
                value={sortDropdownOption.find(o => o.value === selectedSortData.labelKey)}
                onChange={onSortSelect}
                inputContainerClassName={classNames.dropdownContainer}
              />
            </div>
          </div>
        </div>

        <div className={classNames.historyList}>
          {history.map((historyItem, historyIndex) => {
            const mistakesOnAverage = Number(
              (historyItem.numberOfConversations
                ? (historyItem.numberOfMistakes || 0) / historyItem.numberOfConversations
                : 0
              ).toFixed(2)
            );

            const numberOfConversations = historyItem.numberOfConversations || 0;

            return (
              <div
                key={`historyItem--${historyIndex}`}
                className={classes(classNames.historyListItem, classNames.historyListGroupItem)}
              >
                <div>
                  <div className={classNames.historyListItemHeader}>
                    <div className={classNames.historyListItemStars}>
                      <SVG
                        className={classNames.historyListItemStar}
                        src={historyItem.maxRank >= 1 ? starIcon : starEmptyIcon}
                      />
                      <SVG
                        className={classNames.historyListItemStar}
                        src={historyItem.maxRank >= 2 ? starIcon : starEmptyIcon}
                      />
                      <SVG
                        className={classNames.historyListItemStar}
                        src={historyItem.maxRank >= 3 ? starIcon : starEmptyIcon}
                      />
                    </div>
                    <button
                      onClick={() => {
                        if (userId) {
                          openReport(historyItem, userId);
                        }
                      }}
                      className={classNames.seeMyChatsBtn}
                    >
                      {t('my_tutor_page.seeMyChatsBtn')}
                    </button>
                  </div>
                </div>
                <div>
                  <div className={classNames.historyListItemTitel}>{historyItem.topic}</div>
                  <div className={classNames.historyListItemNumberOfConversations}>
                    {t(
                      numberOfConversations === 1
                        ? 'my_tutor_page.youHas1Chat'
                        : 'my_tutor_page.youHasNChat',
                      {
                        numberOfConversations,
                      }
                    )}
                  </div>
                </div>
                <div>
                  {historyItem.maxRank >= 3 && (
                    <div className={classNames.historyListItemYouCanDoIt}>
                      {t('my_tutor_page.youCanDoIt')}
                    </div>
                  )}
                  {historyItem.maxRank < 3 && (
                    <button
                      className={classNames.historyListItemTryAgainBtn}
                      onClick={() => {
                        startConversation(historyItem);
                      }}
                    >
                      {t('my_tutor_page.repeatTopic')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {hasMoreHistory && !isHistoryLoading && (
            <div className={classNames.getMoreHistory}>
              <button onClick={getMoreHistory}>{t('my_tutor_page.showMoreHistory')}</button>
            </div>
          )}
          {isHistoryLoading && (
            <div className={classNames.historyLoader}>
              <AppLoaderCircle />
            </div>
          )}
        </div>
      </div>
    );
  }, [
    t,
    sortDropdownOption,
    selectedSortData,
    onSortSelect,
    history,
    openReport,
    hasMoreHistory,
    isHistoryLoading,
    getMoreHistory,
    userId,
  ]);

  const openQuiz = useCallback(
    (enhancementArea: string, enhancementAreaSimplified: string, status: QuizStatus) => {
      const userId = user?.id;

      const isNumber = (item: any) => {
        return typeof item === 'number';
      };

      const filterNumbers = (arr: any[]) => {
        return arr.filter(n => isNumber(n));
      };

      const getMin = (arr: any[]) => {
        const numbers = filterNumbers(arr);
        return numbers.length > 0 ? Math.min(...numbers) : undefined;
      };

      const getMax = (arr: any[]) => {
        const numbers = filterNumbers(arr);
        return numbers.length > 0 ? Math.max(...numbers) : undefined;
      };

      const fromScore = getMax([
        TutorTopicsUtils.getFromScoreOfQuizStatus(status || QuizStatus.NONE),
        mistakesFilters?.score && typeof mistakesFilters.score !== 'string'
          ? (mistakesFilters.score as NumberRangeFilterValue).min
          : undefined,
      ]);

      const toScore = getMin([
        TutorTopicsUtils.getToScoreOfQuizStatus(status || QuizStatus.NONE),
        mistakesFilters?.score && typeof mistakesFilters.score !== 'string'
          ? (mistakesFilters.score as NumberRangeFilterValue).max
          : undefined,
      ]);

      navigate('/dashboard/my-tutor-chat', {
        state: {
          type: PrivateTutorTypes.QUIZ,
          enhancementArea: enhancementArea,
          enhancementAreaSimplified: enhancementAreaSimplified,
          userId: userId,
          fromScore: fromScore,
          toScore: toScore,
        },
      });
    },
    [navigate, user, mistakesFilters]
  );

  const sortMistakesDropdownOption: Option[] = useMemo(() => {
    return sortMistakesOptions.map(sort => {
      return {
        label: t(sort.labelKey),
        value: sort.labelKey,
        data: sort,
      };
    });
  }, [t]);

  const onMistakesSortSelect = useCallback((value: Option) => {
    setSelectedMistakesSortData(value.data as SortData);
  }, []);

  const mistakesList = useMemo(() => {
    let mistakesList = [...mistakes];

    if (
      mistakesFilters.type &&
      typeof mistakesFilters.type !== 'string' &&
      (mistakesFilters.type as string[]).length > 0
    ) {
      const typesMapper = (mistakesFilters.type as string[]).reduce(
        (acc, item) => {
          acc[item] = true;
          return acc;
        },
        {} as Record<string, boolean>
      );
      mistakesList = mistakesList.filter(
        m => typesMapper[m.enhancementAreaSimplified || m.enhancementArea]
      );
    }

    if (
      mistakesFilters.status &&
      typeof mistakesFilters.status !== 'string' &&
      (mistakesFilters.status as string[]).length > 0
    ) {
      const statusMapper = (mistakesFilters.status as string[]).reduce(
        (acc, item) => {
          acc[item] = true;
          return acc;
        },
        {} as Record<string, boolean>
      );
      mistakesList = mistakesList.filter(m => {
        const status = TutorTopicsUtils.getQuizStatus(m.quizzes[0].Score);
        return statusMapper[t(`my_tutor_page.quizStatus.${status}`).toString()];
      });
    }

    if (mistakesFilters.score && typeof mistakesFilters.score !== 'string') {
      const scoreFilter = mistakesFilters.score as NumberRangeFilterValue;
      const hasMinFilter = typeof scoreFilter.min === 'number';
      const hasMaxFilter = typeof scoreFilter.max === 'number';

      if (hasMinFilter || hasMaxFilter) {
        mistakesList = mistakesList
          .map(_m => {
            const m: GetMistakesResponse = {
              ..._m,
              quizzes: [],
              score: -1,
              numberOfQuizzes: 0,
              numberOfDoneQuizzes: 0,
            };

            (_m.quizzes || []).forEach(quiz => {
              let addToList: boolean = true;

              if (addToList && hasMinFilter && (scoreFilter.min || 0) > quiz.Score) {
                addToList = false;
              }
              if (addToList && hasMaxFilter && (scoreFilter.max || 100) < quiz.Score) {
                addToList = false;
              }

              if (addToList) {
                m.quizzes.push(quiz);

                if (m.score === -1 || m.score > quiz.Score) {
                  m.score = quiz.Score;
                }

                m.numberOfQuizzes += 1;

                if (quiz.IsQuizDone) {
                  m.numberOfDoneQuizzes += 1;
                }
              }
            });

            return m;
          })
          .filter(m => m.quizzes.length > 0);
      }
    }

    mistakesList = mistakesList.sort((m1: GetMistakesResponse, m2: GetMistakesResponse) => {
      let sortKey: number = 0;
      const sortFunc = selectedMistakesSortData.isDescending
        ? TutorTopicsUtils.sortDescending
        : TutorTopicsUtils.sortAscending;

      if (selectedMistakesSortData.field === 'created_date') {
        sortKey = sortFunc(
          new Date(m1.quizzes[0].CreatedDate),
          new Date(m2.quizzes[0].CreatedDate)
        );

        if (sortKey !== 0) {
          return sortKey;
        }
      }

      if (selectedMistakesSortData.field === 'status') {
        const getSortStatusKey = (score: number) => {
          const status = TutorTopicsUtils.getQuizStatus(score);

          switch (status) {
            case QuizStatus.NONE:
              return 0;
            case QuizStatus.PRACTICE:
              return 1;
            case QuizStatus.IMPROVEMENT:
              return 2;
            case QuizStatus.COMPLETED:
              return 3;
          }

          return 0;
        };

        const sortStatusKey1 = getSortStatusKey(m1.quizzes[0].Score);
        const sortStatusKey2 = getSortStatusKey(m2.quizzes[0].Score);

        sortKey = sortFunc(sortStatusKey1, sortStatusKey2);

        if (sortKey !== 0) {
          return sortKey;
        }

        sortKey = TutorTopicsUtils.sortDescending(
          new Date(m1.quizzes[0].CreatedDate),
          new Date(m2.quizzes[0].CreatedDate)
        );

        if (sortKey !== 0) {
          return sortKey;
        }
      }

      if (selectedMistakesSortData.field === 'score') {
        sortKey = sortFunc(m1.score, m2.score);

        if (sortKey !== 0) {
          return sortKey;
        }

        sortKey = TutorTopicsUtils.sortDescending(
          new Date(m1.quizzes[0].CreatedDate),
          new Date(m2.quizzes[0].CreatedDate)
        );

        if (sortKey !== 0) {
          return sortKey;
        }
      }

      return TutorTopicsUtils.sortAscending(
        m1.enhancementAreaSimplified || m1.enhancementArea,
        m2.enhancementAreaSimplified || m2.enhancementArea
      );
    });

    return mistakesList;
  }, [mistakes, mistakesFilters, selectedMistakesSortData, t]);

  const practiceCount = useMemo(
    () =>
      mistakesList.filter(
        m =>
          m.quizzes.length > 0 &&
          TutorTopicsUtils.getQuizStatus(m.quizzes[0].Score) === QuizStatus.PRACTICE
      ).length,
    [mistakesList]
  );

  const mobilePracticeMistake = useMemo<GetMistakesResponse | null>(() => {
    const practice = mistakes.filter(
      m =>
        m.quizzes.length > 0 &&
        TutorTopicsUtils.getQuizStatus(m.quizzes[0].Score) === QuizStatus.PRACTICE
    );

    if (!practice.length) return null;

    return [...practice].sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return (
        new Date(a.quizzes[0].CreatedDate).getTime() - new Date(b.quizzes[0].CreatedDate).getTime()
      );
    })[0];
  }, [mistakes]);

  const mistakesBlock = useMemo(() => {
    if (isMobile) {
      return (
        <CommonMistakes
          mistakes={mobilePracticeMistake ? [mobilePracticeMistake] : []}
          onSelect={openQuiz}
        />
      );
    }

    return (
      <CommonMistakes mistakes={mistakesList} onSelect={openQuiz} headerCount={practiceCount} />
    );
  }, [
    t,
    isMobile,
    mistakes,
    mobilePracticeMistake,
    openQuiz,
    practiceCount,
    mistakesFilterFields,
    mistakesFilters,
    mistakesList,
  ]);

  const showView = useMemo(() => {
    if (isLoading || (isHistoryLoading && isFirstHistoryLoading)) {
      return 'loading';
    }

    return 'self';
  }, [isLoading, isHistoryLoading, isFirstHistoryLoading, hasData, mistakes]);

  const loadingView = useMemo(() => {
    if (showView !== 'loading') {
      return;
    }

    return (
      <div className={classNames.myPrivateTutor}>
        <div className={classNames.fullPage}>
          {teachersClasses}
          <div className={classNames.fullPageContent}>
            <AppLoaderCircle />
          </div>
        </div>
      </div>
    );
  }, [showView, teachersClasses]);

  const selfView = useMemo(() => {
    if (showView !== 'self') {
      return;
    }

    return (
      <div className={classNames.myPrivateTutor}>
        {teachersClasses}
        {(() => {
          const layout = isMobile || windowWidth <= 850 ? 'column' : 'row';
          return (
            <div className={isMobile ? classNames.mobileSection : classNames.topSection}>
              <GoToTutor layout={layout} />
              {mistakesBlock}
            </div>
          );
        })()}
        {historyBlock}
      </div>
    );
  }, [showView, teachersClasses, mistakesBlock, historyBlock, t, windowWidth, isMobile]);

  return (
    <>
      {loadingView}
      {selfView}
    </>
  );
};

export default SelfView;
